import { extractBrokerageFirmDocumentConfigs } from '@folio/utils/getDocumentCategoryLabel';
import { useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  configControllerGetPublicTenantConfigQueryOptions,
  legacyDocumentsControllerGetExpectedUploadedDocumentsQueryOptions,
} from 'korint-sdk';
import { useMemo } from 'react';

type ExpectedUploadedDocumentsTarget =
  | { policyId: string; productId: string; branchId?: string }
  | { brokerageFirmId: string };

export function useExpectedUploadedDocuments(target: ExpectedUploadedDocumentsTarget) {
  const isBrokerageFirm = 'brokerageFirmId' in target;
  const targetId = isBrokerageFirm ? target.brokerageFirmId : target.policyId;
  const branchId = isBrokerageFirm ? undefined : target.branchId;

  const {
    data: expected,
    isPending,
    isError,
  } = useQuery(
    legacyDocumentsControllerGetExpectedUploadedDocumentsQueryOptions({ targetId, branchId }),
  );

  const { data: policyholderDocuments } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(isBrokerageFirm ? '' : target.productId),
    select: data => data.policyholderDocuments ?? [],
    enabled: !isBrokerageFirm,
  });

  const { data: tenantConfig } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    enabled: isBrokerageFirm,
  });

  const documents = useMemo(() => {
    const brokerageFirmConfigs = isBrokerageFirm
      ? extractBrokerageFirmDocumentConfigs(tenantConfig)
      : undefined;
    const documentConfigs = brokerageFirmConfigs ?? policyholderDocuments;

    return expected?.documents.map(({ category, required }) => ({
      category,
      required,
      validity: documentConfigs?.find(config => config.category === category)?.validity,
      allowMultiple: brokerageFirmConfigs?.find(config => config.category === category)
        ?.allowMultiple,
    }));
  }, [expected, isBrokerageFirm, policyholderDocuments, tenantConfig]);

  return { data: documents, isPending, isError };
}
